import { usePhysicsStore } from "../stores/physicsStore";
import { useSceneStore } from "../stores/sceneStore";
import { logger } from "../utils/logger";
import { getPhysicsWorker } from "./workerHandle";
import {
  getLatestMujocoWorkerResourceUrl,
  getMujocoWorkerResourceEntries,
} from "./workerResourceDiagnostics";

const MAX_DIAGNOSTIC_ASSET_EXAMPLES = 10;
const MAX_WORKER_LOG_MESSAGE_LENGTH = 1000;

function redactUrl(value) {
  if (!value) return "";
  try {
    const url = new URL(String(value), globalThis.location?.origin || "http://localhost");
    return `${url.origin}${url.pathname}`;
  } catch {
    return String(value).split(/[?#]/, 1)[0];
  }
}

function summarizeAssetPathList(paths) {
  const values = Array.isArray(paths) ? paths.map((path) => String(path)) : [];
  return {
    count: values.length,
    examples: values.slice(0, MAX_DIAGNOSTIC_ASSET_EXAMPLES),
    omitted: Math.max(0, values.length - MAX_DIAGNOSTIC_ASSET_EXAMPLES),
  };
}

function sanitizeDiagnosticData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const sanitized = { ...data };
  if (Array.isArray(sanitized.aliases)) {
    sanitized.aliases = summarizeAssetPathList(sanitized.aliases);
  }
  if (Array.isArray(sanitized.assetPaths)) {
    sanitized.assetPaths = summarizeAssetPathList(sanitized.assetPaths);
  }
  if (Array.isArray(sanitized.missingAssets)) {
    sanitized.missingAssets = summarizeAssetPathList(sanitized.missingAssets);
  }
  if (Array.isArray(sanitized.blockedAssets)) {
    sanitized.blockedAssets = summarizeAssetPathList(sanitized.blockedAssets);
  }
  return sanitized;
}

function sanitizeDiagnostics(diagnostics) {
  return (diagnostics || []).slice(-50).map((entry) => ({
    ...entry,
    data: sanitizeDiagnosticData(entry?.data),
  }));
}

function sanitizeWorkerLogs(workerLogs) {
  if (!workerLogs || typeof workerLogs !== "object") return workerLogs;
  const logs = Array.isArray(workerLogs.logs)
    ? workerLogs.logs.map((entry) => {
        const message = String(entry?.message || "");
        return {
          ...entry,
          message:
            message.length > MAX_WORKER_LOG_MESSAGE_LENGTH
              ? `${message.slice(0, MAX_WORKER_LOG_MESSAGE_LENGTH)}... [truncated]`
              : message,
        };
      })
    : workerLogs.logs;
  return { ...workerLogs, logs };
}

function sanitizeWorkerFailureDetails(details) {
  if (!details || typeof details !== "object") return details || null;
  return {
    ...details,
    url: details.url ? redactUrl(details.url) : undefined,
    workerScriptUrl: details.workerScriptUrl ? redactUrl(details.workerScriptUrl) : undefined,
    workerLogs: sanitizeWorkerLogs(details.workerLogs),
  };
}

function sanitizeWorkerResourceEntries(entries) {
  return (entries || []).map((entry) => ({
    ...entry,
    name: redactUrl(entry?.name || ""),
  }));
}

async function readPhysicsFailureHeader(path) {
  try {
    const response = await fetch(path, {
      cache: "no-store",
      credentials: "same-origin",
    });
    return {
      url: redactUrl(response.url || path),
      ok: response.ok,
      status: response.status,
      coop: response.headers.get("Cross-Origin-Opener-Policy") || "",
      coep: response.headers.get("Cross-Origin-Embedder-Policy") || "",
      corp: response.headers.get("Cross-Origin-Resource-Policy") || "",
      contentType: response.headers.get("Content-Type") || "",
      cacheControl: response.headers.get("Cache-Control") || "",
      cfCacheStatus: response.headers.get("CF-Cache-Status") || "",
      age: response.headers.get("Age") || "",
    };
  } catch (error) {
    return {
      url: redactUrl(path),
      ok: false,
      status: "fetch-error",
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export function safeJsonStringify(value) {
  const seen = new WeakSet();
  return JSON.stringify(
    value,
    (_key, entry) => {
      if (entry instanceof Error) {
        return {
          name: entry.name,
          message: entry.message,
          stack: entry.stack,
        };
      }
      if (typeof entry === "function") return `[Function ${entry.name || "anonymous"}]`;
      if (entry && typeof entry === "object") {
        if (seen.has(entry)) return "[Circular]";
        seen.add(entry);
      }
      return entry;
    },
    2
  );
}

function copyTextWithHiddenTextarea(text) {
  if (typeof document === "undefined") return false;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
  }
}

export async function copyTextToClipboard(text, logPrefix = "[physicsFailureReport]") {
  try {
    if (copyTextWithHiddenTextarea(text)) return true;
  } catch (error) {
    logger.warn(`${logPrefix} Hidden textarea copy failed`, error);
  }

  try {
    if (globalThis.navigator?.clipboard?.writeText) {
      await globalThis.navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    logger.warn(`${logPrefix} Clipboard API copy failed`, error);
  }

  return false;
}

export async function buildPhysicsFailureReportText() {
  const scene = useSceneStore.getState();
  const physics = usePhysicsStore.getState();
  const currentPath = globalThis.location?.pathname || "/";
  const workerResourceUrl = getLatestMujocoWorkerResourceUrl();
  const workerResourceEntries = getMujocoWorkerResourceEntries();
  const report = {
    generatedAt: new Date().toISOString(),
    pageUrl: redactUrl(globalThis.location?.href || ""),
    runtime: {
      userAgent: globalThis.navigator?.userAgent || "",
      platform: globalThis.navigator?.platform || "",
      secureContext: globalThis.isSecureContext === true,
      crossOriginIsolated: globalThis.crossOriginIsolated === true,
      webAssembly: typeof WebAssembly !== "undefined",
      sharedArrayBuffer: typeof SharedArrayBuffer !== "undefined",
    },
    headers: {
      document: await readPhysicsFailureHeader(currentPath),
      worker: workerResourceUrl ? await readPhysicsFailureHeader(workerResourceUrl) : null,
      mujocoJs: await readPhysicsFailureHeader("/mujoco3/mujoco.js"),
      mujocoWasm: await readPhysicsFailureHeader("/mujoco3/mujoco.wasm"),
    },
    physics: {
      status: physics.status,
      lastError: physics.lastError,
      engine: physics.engine,
      isRunning: physics.isRunning,
      isPaused: physics.isPaused,
      loadMode: physics.loadMode,
      mappedBodies: physics.mappedBodies,
      totalBodies: physics.totalBodies,
      timestep: physics.timestep,
      realtimeScale: physics.realtimeScale,
      workerPresent: Boolean(getPhysicsWorker()),
      workerResourceEntries: sanitizeWorkerResourceEntries(workerResourceEntries),
      lastWorkerFailureDetails: sanitizeWorkerFailureDetails(physics.lastWorkerFailureDetails),
      diagnostics: sanitizeDiagnostics(physics.diagnostics),
    },
    scene: {
      bodies: Object.keys(scene.bodies || {}).length,
      joints: Object.keys(scene.joints || {}).length,
      geoms: Object.keys(scene.geoms || {}).length,
      sites: Object.keys(scene.sites || {}).length,
      actuators: Object.keys(scene.actuators || {}).length,
      tendons: Object.keys(scene.tendons || {}).length,
      worldBodies: Array.isArray(scene.worldBodies) ? scene.worldBodies.length : 0,
    },
  };

  return safeJsonStringify(report);
}
